<template>
  <Container
    class="board-list-dnd-container-drop-zone"
    group-name="lists"
    behaviour="drop-zone"
    @drop="onContainerDrop({ obj: list, items: cards }, $event)"
  >
    <div class="board-list" :class="{ dragmode: isDraggingCard }">
      <div class="draggable-list-handle board-list-title-container d-flex flex-row pr-0">
        <span class="title flex-fill font-weight-bold" @dblclick="startEdit()">{{ list.title }}</span>

        <Menu class="pl-1" icon="ellipsis-v" :title="list.cards.length">
          <ul class="board-list-menu p-1">
            <li class="p-1">
              <span class="p-1 clickable hover-highlight" @click="moveAllCardsToNextList()">Все карточки в следующий лист</span>
            </li>
            <li class="p-1">
              <span class="p-1 clickable hover-highlight" @click="moveAllCardsToPrevList()">Все карточки в предыдущий лист</span>
            </li>
            <li class="divider">&nbsp;</li>
            <li class="p-1" v-for="list in board.lists.filter(l => l != list)" :key="list.id">
              <span class="p-1 clickable hover-highlight text-truncate" @click="moveAllCardsToList(list)"
                >Все карточки в '{{ list.title }}'</span
              >
            </li>
          </ul>
        </Menu>
      </div>

      <ListFields class="board-list-fields" :list="list" />

      <div class="board-list-scroll-container">
        <Container
          group-name="lists"
          :get-child-payload="getChildPayload"
          @drag-start="onDragStart"
          @drop="onDrop({ obj: list, items: cards }, $event)"
          @drag-end="onDragEnd"
          :animation-duration="200"
          :drop-placeholder="dropPlaceholderOptions"
          drag-class="card-ghost"
          drop-class="card-ghost-drop"
        >
          <Draggable class="cards-draggable-wrapper" v-for="card in cards" :key="'card_' + card.id">
            <Card :card="card" :isSelected="selectedCardIds.includes(card.id)" @click="onCardClick"></Card>
          </Draggable>
        </Container>
      </div>
      <AddNewItem
        class="add-new-card list-add-new-card"
        title="Добавить карточку"
        watermark="Название карточки"
        @submit="addNewCard($event)"
      />
    </div>
  </Container>
</template>

<script>
import { mapGetters } from "vuex";

import { indexOf, removeElementFromArray } from "@/common/utils.array";

import SmoothDndHelpers from "@/common/smoothDndHelpers";
import { EDIT_LIST, EDIT_CARD } from "@/store/actions.type";

import Menu from "@/components/general/Menu.vue";
import ListFields from "./ListFields.vue";
import Card from "./Card.vue";
import AddNewItem from "./AddNewItem.vue";
import cache from "../../store/cache";

export default {
  name: "List",
  components: { ListFields, Card, AddNewItem, Menu },
  props: { list: { required: true } },
  computed: {
    ...mapGetters(["isMobileDevice", "isDraggingCard"]),
    board() {
      return cache.boards.byId(this.list.board_id);
    },
    cards() {
      if (!this.list || !this.list.cards) {
        return [];
      }

      return this.list.cards;
    },
    dropPlaceholderOptions() {
      return {
        className: "drop-preview",
        animationDuration: "150",
        showOnTop: true
      };
    }
  },
  created() {
    this.$eventHub.$on("command-clear-card-selection", () => {
      this.selectedCardIds.length = 0;
    });
  },
  beforeDestroy() {
    this.$eventHub.$off("command-clear-card-selection");
  },
  methods: {
    getChildPayload(index) {
      const item = this.cards[index];
      const payload = {
        index: index,
        obj: this.list,
        item: item,
        items: this.cards,
        extraItems: this.selectedCardIds.filter(id => id != item.id).map(id => cache.cards.byId(id))
      };

      return payload;
    },
    onDragStart(event) {
      this.smoothDndHelpers.onDragStart(event);
    },
    onDragEnd(event) {
      this.smoothDndHelpers.onDragEnd(event);
    },
    onContainerDrop(dest, event) {
      this.smoothDndHelpers.onContainerDrop(dest, event);
    },
    onDrop(dest, event) {
      this.smoothDndHelpers.onDrop(dest, event);
    },
    startEdit() {
      this.$store.dispatch(EDIT_LIST, this.list);
    },
    addNewCard(title) {
      if (!title) return;

      this.$store.dispatch("cards/new", { list: this.list, title: title });
    },
    moveAllCardsToList(list) {
      if (!list || !this.board) {
        return;
      }

      if (!this.cards.length) {
        this.$snotify.success(`В этом списке нет карточек`, {
          timeout: 1000,
          showProgressBar: false
        });
        return;
      }

      this.$modal.show("ConfirmationDialog", {
        message: `Точно переместить все карточки из листа <b>${this.list.title}</b> в <b>${list.title}</b>?`,
        yes: () => {
          this.$store.dispatch("lists/moveCardsTo", {
            list: this.list,
            newList: list
          });
        }
      });
    },
    moveAllCardsToPrevList() {
      if (!this.cards.length) {
        this.$snotify.success(`В этом списке нет карточек`, {
          timeout: 1000,
          showProgressBar: false
        });
        return;
      }

      const board = cache.boards.byId(this.list.board_id);
      if (!board) return;

      const index = indexOf(board.lists, l => l.id == this.list.id);
      if (index < 0) return;

      const prevList = board.lists[index - 1];
      if (!prevList) {
        this.$snotify.success(`Этот список уже первый`, {
          timeout: 1000,
          showProgressBar: false
        });
        return;
      }

      const _this = this;
      this.$modal.show("ConfirmationDialog", {
        message: `Точно переместить все карточки из листа <b>${_this.list.title}</b> в <b>${prevList.title}</b>?`,
        yes: () => {
          _this.$store.dispatch("lists/moveCardsTo", {
            list: _this.list,
            newList: prevList
          });
        }
      });
    },
    moveAllCardsToNextList() {
      if (!this.cards.length) {
        this.$snotify.success(`В этом списке нет карточек`, {
          timeout: 1000,
          showProgressBar: false
        });
        return;
      }

      const board = cache.boards.byId(this.list.board_id);
      if (!board) return;

      const index = indexOf(board.lists, l => l.id == this.list.id);
      if (index < 0) return;

      const nextList = board.lists[index + 1];
      if (!nextList) {
        this.$snotify.success(`Этот список уже последний`, {
          timeout: 1000,
          showProgressBar: false
        });
        return;
      }

      const _this = this;
      this.$modal.show("ConfirmationDialog", {
        message: `Точно переместить все карточки из листа <b>${this.list.title}</b> в <b>${nextList.title}</b>?`,
        yes: () => {
          _this.$store.dispatch("lists/moveCardsTo", {
            list: _this.list,
            newList: nextList
          });
        }
      });
    },
    onCardClick(card) {
      if (!card) return;

      if (this.$store.state.keyboard.alt) {
        if (!removeElementFromArray(this.selectedCardIds, card.id)) {
          this.selectedCardIds.push(card.id);
        }
      } else {
        this.$store.dispatch(EDIT_CARD, card);
      }
    }
  },
  data() {
    return {
      smoothDndHelpers: new SmoothDndHelpers(this.$order),
      selectedCardIds: []
    };
  },
  mounted() {
    const _this = this;
    this.smoothDndHelpers.dragStartedCallbacks.push(() => {
      _this.$store.commit("DRAG_CARD", true);
    });

    this.smoothDndHelpers.onDropGuards.push(() => {
      return !_this.isDraggingCard;
    });

    this.smoothDndHelpers.dragEndIfNotDropCallbacks.push(() => {
      _this.$store.commit("DRAG_CARD", false);
    });

    this.smoothDndHelpers.beforeDropCallbacks.push(() => {
      _this.$store.commit("DRAG_CARD", false);
    });

    this.smoothDndHelpers.fromArchiveDropCallbacks.push(item => {
      _this.$store.dispatch("cards/dearchive", item);
    });

    this.smoothDndHelpers.droppedCallbacks.push(payload => {
      _this.$store.dispatch("cards/move", {
        list: payload.obj,
        card: payload.item,
        order: payload.order
      });
    });
  }
};
</script>
<style lang="postcss">
.board-list-dnd-container-drop-zone {
}
.board-list {
  display: grid;
  grid-template-areas:
    "header"
    "fields"
    "cards"
    "add";
  grid-template-columns: 1fr;
  grid-template-rows: auto auto 1fr auto;
  max-height: var(--board-list-max-height);
  width: 272px;
  padding: 8px 7px 8px 8px; /* 7px cause .board-list-scroll-container has padding-right 1px */

  border-radius: 8px;
  background: var(--bg-list);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
  color: var(--color-list);

  &.dragmode {
    height: var(--board-list-max-height);
  }

  .board-list-scroll-container {
    padding-right: 1px;
    grid-row: 3;
    overflow-y: auto;
    max-height: 100%;

    .smooth-dnd-container {
      min-height: 0px;
    }
  }

  &-menu {
    background: var(--bg-list);
    color: var(--color-list);
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 70vh;
    max-width: 90vw;
  }

  &-title-container {
    margin: 0 0 10px;
    padding: 5px;
    border-bottom: 1px dashed #ddd;
    color: #999;

    .title {
      font-size: 14px;
      text-transform: uppercase;
      text-overflow: ellipsis;
      user-select: none;
    }
  }

  &-fields {
    grid-area: fields;
  }

  .cards-draggable-wrapper {
    margin-bottom: 2px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  &-cards-counter {
    font-weight: normal;
    font-size: 13px;
  }

  .card {
    position: relative;
    border-radius: 5px;
    background-color: var(--bg-card);
    border: 1px solid var(--border-card);
    padding: 8px 10px;

    .title {
      display: block;
      padding-right: 5px;
    }
  }

  .list-add-new-card {
    grid-row: 4;
    position: relative;
    border-radius: 5px;

    padding: 8px 10px 2px 10px;
    color: var(--color-list);

    &:hover {
      background: rgba(0, 0, 0, 0.12);
    }
  }
}
</style>
